<!-- container -->
<div
    class="h-full w-full rounded-md bg-neutral-900 text-slate-100 p-2 relative overflow-y-auto flex flex-col min-h-0 border border-neutral-800/50 border-dashed"
    [class.border-gray-500/60]="isDragActive"
    (dragenter)="onDragEnterContainer($event)"
    (dragleave)="onDragLeaveContainer($event)"
    (dragover)="onDragOverWindow($event)"
    (drop)="onDropContainer($event)">
  <input
    #fileInput
    type="file"
    multiple
    aria-hidden="true"
    tabindex="-1"
    class="absolute h-px w-px opacity-0 pointer-events-none"
    (change)="onFileSelected($event)" />

  <!-- header / path + filter -->
  <div class="mb-4 flex flex-wrap items-center gap-3">
    <div class="flex items-center gap-2 text-lg font-semibold">
      <div
        class="group rounded-lg border border-neutral-700/80 bg-neutral-900 px-3 py-2 min-w-[64px] text-center transition duration-150 cursor-pointer"
        [class.border-slate-500/70]="activeBreadcrumbIndex === null"
        [class.bg-slate-600/20]="activeBreadcrumbIndex === null"
        (dragenter)="onBreadcrumbDragEnter($event, rootPath || '', null)"
        (dragleave)="onBreadcrumbDragLeave($event, null)"
        (dragover)="onBreadcrumbDragOver($event, rootPath || '', null)"
        (drop)="onBreadcrumbDrop($event, rootPath || '', null)">
        <button
          class="w-full text-lg font-semibold leading-none"
          type="button"
          (click)="goRoot()">
          /
        </button>
      </div>

      @for (crumb of breadcrumbDisplayData; track crumb.path; let i = $index) {
        <div class="inline-flex items-center gap-2">
          <span class="opacity-60 text-lg select-none">›</span>
          <div
            class="group rounded-lg border border-transparent px-3 py-2 transition duration-150 cursor-pointer"
            [class.border-slate-500/70]="activeBreadcrumbIndex === i"
            [class.bg-slate-600/20]="activeBreadcrumbIndex === i"
            (dragenter)="onBreadcrumbDragEnter($event, crumb.path, i)"
            (dragleave)="onBreadcrumbDragLeave($event, i)"
            (dragover)="onBreadcrumbDragOver($event, crumb.path, i)"
            (drop)="onBreadcrumbDrop($event, crumb.path, i)">
            <button
              class="text-lg font-semibold leading-tight"
              type="button"
              (click)="goToCrumb(i)">
              {{ crumb.label }}
            </button>
          </div>
        </div>
      }
    </div>

    @if (!fileOnly) {
      <div class="flex flex-wrap items-center gap-3 w-full lg:w-auto">
        <input
          class="w-full md:w-72 lg:w-80 rounded-md border border-neutral-600/80 bg-neutral-950/95 px-3 py-2 text-slate-100 shadow-inner shadow-black/30 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/60"
          [placeholder]="'Documents.FilterPlaceholder' | localizeText : 'Filter directories…'"
          [value]="dirFilter || ''"
          (input)="onFilterInput($event)" />

        <app-button
          buttonType="grey-with-border"
          (click)="refreshRequested.emit()">
          <span class="flex items-center gap-1.5 text-xs font-medium">
            <app-icon icon="mdi-refresh" size="3.5"></app-icon>
            <span>Refresh</span>
          </span>
        </app-button>

        <div class="relative">
          <app-button
            #actionButton
            class="rounded-full px-4 py-2 text-sm font-semibold shadow-lg"
            buttonType="primary"
            (click)="toggleActionsMenu()"
            [attr.aria-expanded]="actionsMenuOpen">
            <span class="flex items-center gap-2">
              <app-icon icon="mdi-plus" size="3"></app-icon>
              <span class="leading-none">New</span>
            </span>
          </app-button>
          @if (actionsMenuOpen) {
            <div
              #dropdown
              class="absolute right-0 mt-2 w-48 rounded-xl border border-neutral-800 bg-neutral-900 shadow-[0_10px_30px_rgba(0,0,0,0.6)] backdrop-blur-xl z-20">
              <button
                type="button"
                class="flex w-full items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-slate-200 hover:bg-neutral-800 transition"
                (click)="openDropdownOption('upload')">
                <span class="flex items-center gap-2">
                  <app-icon icon="mdi-file-upload-outline" size="3"></app-icon>
                  <span>Upload a file</span>
                </span>
              </button>
              <div class="border-t border-neutral-800"></div>
              <button
                type="button"
                class="flex w-full items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-slate-200 hover:bg-neutral-800 transition"
                (click)="openDropdownOption('folder')">
                <span class="flex items-center gap-2">
                  <app-icon icon="mdi-folder-plus-outline" size="3"></app-icon>
                  <span>Create folder</span>
                </span>
              </button>
              <div class="border-t border-neutral-800"></div>
              <button
                type="button"
                class="flex w-full items-center justify-between gap-3 px-4 py-3 text-sm font-medium text-slate-200 hover:bg-neutral-800 transition disabled:opacity-40 disabled:cursor-not-allowed"
                [disabled]="!docs || docs.length === 0 || loadingExport"
                (click)="exportAll(); actionsMenuOpen = false">
                <span class="flex items-center gap-2">
                  <app-icon icon="mdi-folder-zip-outline" size="3"></app-icon>
                  <span>Export all as ZIP</span>
                </span>
              </button>
            </div>
          }
        </div>
        @if (isCreatingFolder) {
          <div class="flex flex-wrap items-center gap-2 rounded-lg border border-neutral-700/80 bg-neutral-900/80 px-2 py-1 shadow-inner">
            <input
              #newFolderInput
              class="min-w-[180px] flex-1 rounded-md border border-neutral-700/80 bg-neutral-900 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none"
              placeholder="Folder name"
              [value]="newFolderName"
              (keydown.enter)="confirmCreateFolder()"
              (input)="onNewFolderInput($event)" />
            <div class="flex items-center gap-1">
              <app-button
                [btnTxt]="'Documents.Create' | localizeText : 'Create'"
                buttonType="primary"
                (click)="confirmCreateFolder()"
                class="text-xs">
              </app-button>
              <button
                class="text-xs text-slate-400 hover:text-white"
                (click)="cancelCreateFolder()"
                type="button">
                Cancel
              </button>
            </div>
          </div>
        }
      </div>
    } @else {
      <div class="flex flex-col gap-3">
        <app-button
          class="rounded-full px-4 py-2 text-sm font-semibold shadow-lg self-start"
          buttonType="primary"
          [disabled]="uploadsDisabled"
          (click)="openDropdownOption('upload')">
          <span class="flex items-center gap-2">
            <app-icon icon="mdi-file-upload-outline" size="3"></app-icon>
            <span class="leading-none">Upload files</span>
          </span>
        </app-button>
      </div>
    }

    <!-- upload status -->
    @if (!loadingFiles && (loadingUpload || lastUploadMsg)) {
      <div class="text-sm">
        @if (loadingUpload) {
          <div class="flex items-center gap-2">
            <app-loader-small></app-loader-small>
            <span class="opacity-80">
              Uploading {{ uploadingFileCount }} file{{ uploadingFileCount === 1 ? '' : 's' }}
            </span>
          </div>
        }
        @if (lastUploadMsg && !loadingUpload) {
          <div class="opacity-70">{{ lastUploadMsg }}</div>
        }
      </div>
    }
  </div>

  <!-- content -->
  <div class="bg-neutral-900/60 overflow-y-auto relative flex flex-col min-h-0 flex-1 w-full">
    <div class="flex items-center justify-between gap-3 px-2 py-1 border-b border-neutral-800/50 text-xs uppercase tracking-wide opacity-70">
      <span>{{ 'Documents.Items' | localizeText : 'Items' }}</span>
      <div class="flex items-center gap-2">
        @if (selectedDocIds.size > 0) {
          <span class="rounded-full border border-sky-500/40 bg-sky-950/40 px-2.5 py-0.5 text-[11px] font-semibold text-sky-300 normal-case tracking-normal">
            {{ selectedCount }} selected
          </span>
        }
        <span class="rounded-full border border-neutral-700/80 bg-neutral-900 px-2 py-0.5 text-[11px] font-medium text-slate-200 normal-case tracking-normal">
          {{ docs.length }} file{{ docs.length === 1 ? '' : 's' }}
        </span>
      </div>
    </div>

    @if (selectedDocIds.size > 0) {
      <div class="flex items-center justify-between gap-3 px-3 py-2.5 bg-slate-900/90 border-b border-slate-700/80 text-xs">
        <div class="flex items-center gap-2 text-slate-200">
          <app-icon icon="mdi-checkbox-marked-circle-outline" size="4" class="text-sky-400"></app-icon>
          <span class="font-semibold text-slate-100">{{ selectedCount }} file{{ selectedCount === 1 ? '' : 's' }} selected</span>
          <span class="text-slate-600">•</span>
          <button
            type="button"
            class="text-slate-400 hover:text-white underline cursor-pointer"
            (click)="clearSelection()">
            Deselect all
          </button>
        </div>
        <div class="flex items-center gap-2">
          <app-button
            buttonType="primary"
            [disabled]="loadingExport"
            (click)="exportSelected()">
            <span class="flex items-center gap-1.5 text-xs font-semibold">
              @if (loadingExport) {
                <app-loader-small></app-loader-small>
                <span>Exporting ZIP…</span>
              } @else {
                <app-icon icon="mdi-folder-zip-outline" size="3.5"></app-icon>
                <span>Export ZIP ({{ selectedCount }})</span>
              }
            </span>
          </app-button>
        </div>
      </div>
    }

    @if (!rows.length) {
      <div class="p-6 text-sm opacity-60">
        {{ 'Documents.NoItems' | localizeText : 'No items' }}
      </div>
    }

    @if (rows.length) {
      <div class="overflow-y-auto flex-1 min-h-0">
            <div class="px-2 pb-3">
          <table class="w-full text-sm select-none">
          <thead class="bg-neutral-900/70 border-b border-neutral-800/50">
            <tr class="text-left">
              <th class="w-10 px-2 py-1 text-center select-none">
                <input
                  type="checkbox"
                  class="h-4 w-4 rounded border-slate-600 bg-slate-900 text-sky-500 accent-sky-500 focus:ring-sky-500 cursor-pointer"
                  [checked]="isAllSelected"
                  [indeterminate]="isSomeSelected"
                  (change)="toggleSelectAll($any($event.target).checked)"
                  [disabled]="!docs || docs.length === 0"
                  aria-label="Select all files" />
              </th>
              <th class="px-2 py-1 font-semibold select-none">{{ 'Documents.ColName' | localizeText : 'Name' }}</th>
              <th class="px-2 py-1 font-semibold select-none">{{ 'Documents.ColKind' | localizeText : 'Kind' }}</th>
              <th class="px-2 py-1 font-semibold select-none">{{ 'Documents.ColSize' | localizeText : 'Size' }}</th>
              <th class="px-2 py-1 font-semibold select-none">{{ 'Documents.ColModified' | localizeText : 'Modified' }}</th>
              <th class="px-2 py-1 font-semibold text-right select-none">{{ 'Documents.ColActions' | localizeText : 'Actions' }}</th>
            </tr>
          </thead>
          <tbody>
            @for (pending of pendingUploads; track $index) {
              <tr class="border-b border-neutral-800/50 bg-neutral-800/30 select-none">
                <td class="w-10 px-2 py-1 text-center select-none"></td>
                <td class="px-2 py-1 select-none">
                  <div class="flex items-center gap-2">
                    <span class="w-5 text-center">⏳</span>
                    <div>
                      <div class="font-medium">{{ pending.name }}</div>
                      <div class="text-xs uppercase tracking-wide text-indigo-400">Uploading…</div>
                    </div>
                  </div>
                </td>
                <td class="px-2 py-1 opacity-70 select-none">Uploading…</td>
                <td class="px-2 py-1 select-none">{{ pending.size ? humanSize(pending.size) : '—' }}</td>
                <td class="px-2 py-1 opacity-70 select-none">—</td>
                <td class="px-2 py-1 text-right text-xs uppercase tracking-wide text-indigo-500">
                  Pending
                </td>
              </tr>
            }
            @for (r of rows; track r.id) {
            <tr
              class="border-b border-neutral-800/50 hover:bg-neutral-800/30 select-none"
              [class.border-slate-500/60]="activeDropDirId === r.id"
              [class.bg-slate-600/20]="activeDropDirId === r.id"
              (dragenter)="isDir(r) && onDirDragEnter($event, r)"
              (dragleave)="isDir(r) && onDirDragLeave($event, r)"
              (dragover)="isDir(r) && onDirDragOver($event, r)"
              (drop)="isDir(r) && onDirDrop($event, r)"
              [attr.draggable]="isFile(r) && renamingRowId !== r.id ? true : null"
              (dragstart)="isFile(r) && renamingRowId !== r.id && onFileDragStart($event, r)"
              (dragend)="isFile(r) && onFileDragEnd()"
              [attr.data-dir-row-id]="isDir(r) ? r.id : null"
              [attr.data-dir-row-path]="isDir(r) ? r.path : null">
                <!-- Checkbox -->
                <td class="w-10 px-2 py-1 text-center select-none">
                  @if (isFile(r)) {
                    <input
                      type="checkbox"
                      class="h-4 w-4 rounded border-slate-600 bg-slate-900 text-sky-500 accent-sky-500 focus:ring-sky-500 cursor-pointer"
                      [checked]="selectedDocIds.has(r.id)"
                      (click)="$event.stopPropagation()"
                      (change)="toggleDocSelection(r.id, $event)"
                      aria-label="Select file" />
                  }
                </td>
                <!-- Name (folder/file) -->
                <td class="px-2 py-1 select-none">
                  @if (renamingRowId !== r.id) {
                    <div class="relative flex items-center gap-2 group hover:underline cursor-pointer"
                         role="button"
                         (click)="openRow(r)">
                      <span class="w-5 text-center">{{ isDir(r) ? '📁' : '📄' }}</span>
                      <span class="font-medium flex-1 text-ellipsis overflow-hidden whitespace-nowrap">{{ r.name }}</span>
                      @if (isDir(r) && r.isShared) {
                        <span
                          class="inline-flex items-center gap-1 rounded-md bg-sky-500/10 border border-sky-500/20 px-2 py-0.5 text-xs text-sky-300 ml-2"
                          appHoverTooltipCard
                          [appHoverTooltipCardTitle]="'Active Shared Folder'"
                          [appHoverTooltipCardLines]="getShareTooltipLines(r)">
                          <app-icon icon="mdi-folder-account-outline" size="3.5" class="text-sky-400"></app-icon>
                          Shared
                        </span>
                      }
                      @if (isFile(r) && activeCategory === 'referrals') {
                        <span class="inline-flex items-center gap-1 rounded-md bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 text-xs text-amber-300 ml-2">
                          Quarantined / Pending Review
                        </span>
                      }
                      @if (isFile(r) || isDir(r)) {
                        <button
                          class="flex items-center justify-center rounded-full p-1 text-xs text-slate-300 opacity-0 transition duration-150 group-hover:opacity-100 hover:text-white focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-blue-400"
                          type="button"
                          aria-label="Rename item"
                          (click)="startRenaming(r); $event.stopPropagation()">
                          ✏️
                        </button>
                      }
                    </div>
                  } @else {
                    <div class="flex items-center gap-2">
                      <span class="w-5 text-center">{{ isDir(r) ? '📁' : '📄' }}</span>
                      <input
                        #renameInput
                        class="w-full flex-1 rounded-md border border-neutral-700/60 bg-neutral-900 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none"
                        [value]="renameDraft"
                        (input)="onRenameInput($event)"
                        (keydown)="onRenameKeydown($event)"
                        (click)="$event.stopPropagation()"
                        (mousedown)="$event.stopPropagation()"
                        placeholder="{{ 'Documents.RenamePlaceholder' | localizeText : 'Rename file' }}" />
                    </div>
                  }
                </td>
                <!-- Kind -->
                <td class="px-2 py-1 opacity-70 select-none">
                  @if (isDir(r)) {
                    {{ 'Documents.KindFolder' | localizeText : 'Folder' }}
                  } @else {
                    {{ $any(r).mime || ('Documents.KindFile' | localizeText : 'File') }}
                  }
                </td>
                <!-- Size -->
                <td class="px-2 py-1 select-none">
                  @if (isFile(r)) {
                    {{ humanSize(r.size) }}
                  } @else {
                    —
                  }
                </td>
                <!-- Modified -->
                <td class="px-2 py-1 select-none">{{ fmtDate(r.updatedAt) }}</td>
                <!-- Actions -->
                <td class="px-2 py-1 text-right select-none">
                  @if (isDir(r)) {
                    <div class="inline-flex items-center gap-2 justify-end">
                      <app-button
                        class="select-none"
                        [btnTxt]="'Documents.Open' | localizeText : 'Open'"
                        buttonType="grey-with-border"
                        (click)="openRow(r)">
                      </app-button>
                      @if (activeCategory === 'all') {
                        <app-button
                          class="select-none"
                          [btnTxt]="'Share'"
                          buttonType="secondary"
                          (click)="shareFolderRequested.emit({ id: r.id, name: r.name, path: r.path })">
                        </app-button>
                      }
                    </div>
                  } @else {
                    <div class="inline-flex items-center gap-2 justify-end">
                      @if (activeCategory === 'referrals') {
                        <app-button
                          class="select-none"
                          [btnTxt]="'Ingest & Organise'"
                          buttonType="primary"
                          (click)="ingestDocumentRequested.emit(getDocById(r.id))">
                        </app-button>
                      }
                      <app-button
                        class="select-none"
                        [btnTxt]="'Documents.Download' | localizeText : 'Download'"
                        buttonType="primary"
                        (click)="download(r.id)">
                      </app-button>
                      @if (allowDelete) {
                        <app-button
                          class="select-none"
                          [btnTxt]="'Documents.Delete' | localizeText : 'Delete'"
                          buttonType="danger"
                          (click)="delete(r.id)">
                        </app-button>
                      }
                    </div>
                  }
                </td>
              </tr>
            }
          </tbody>
        </table>
        </div>
        <div class="h-4"></div>
      </div>
    }
    @if (loadingFiles) {
      <div class="absolute inset-0 rounded-xl bg-neutral-900/80 z-10 flex items-center justify-center">
        <div class="flex flex-col items-center gap-2">
          <app-loader height="h-12" width="w-12"></app-loader>
          <div class="text-xs text-slate-300 uppercase tracking-wider">
            {{ 'Documents.LoadingFiles' | localizeText : 'Loading files…' }}
          </div>
        </div>
      </div>
    }
  </div>

  <div
    aria-hidden="true"
    class="pointer-events-none absolute inset-0 rounded-xl bg-white/10 backdrop-blur-[16px] border border-white/10 shadow-[0_0_30px_rgba(15,23,42,0.45)] transition-opacity duration-200 flex items-center justify-center px-6 text-center text-xl font-semibold text-slate-900 tracking-wide leading-tight"
    [class.opacity-0]="!isDragActive"
    [class.opacity-100]="isDragActive">
    <div class="max-w-xl">
      Drop files anywhere in this pane to upload them
    </div>
  </div>
</div>
